import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MeetingSummaryResult } from '../../../../models/nova-voice.model';
import type { CardResponse, Section } from '@omni/ai/features/card.schema';
import type { TokenUsage } from '@omni/ai/transports/ai-transport';
import type { NovaFeedbackEvent } from '../../nova-feedback-footer/nova-feedback-footer.component';
import { NovaCardComponent } from '../../nova-card/nova-card.component';

@Component({
  selector: 'nova-conversational',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, NovaCardComponent],
  templateUrl: './conversational.component.html',
  styleUrls: ['./conversational.component.scss'],
})
export class NovaConversationalComponent {
  @Input() hcpName: string = '';
  @Input() capturedTranscript: string = '';
  @Input() analysisData: MeetingSummaryResult | null = null;
  @Input() isProcessing: boolean = false;
  /** Feedback correlation for the analysis turn. Null on a restored analysis — the footer stays hidden. */
  @Input() responseId: string | null = null;
  @Input() usage: TokenUsage | null = null;
  /** Re-run analysis on the existing transcript (no new recording). */
  @Output() reAnalyze = new EventEmitter<void>();
  /** Thumbs vote on the analysis that produced this overview. */
  @Output() feedback = new EventEmitter<NovaFeedbackEvent>();
  /** Discard and capture a brand-new transcription. */
  @Output() reRecord = new EventEmitter<void>();

  readonly #translate = inject(TranslateService);

  get hasTranscript(): boolean {
    return !!this.capturedTranscript?.trim();
  }

  get wordCount(): number {
    if (!this.hasTranscript) return 0;
    return this.capturedTranscript.trim().split(/\s+/).length;
  }

  get estimatedMinutes(): number {
    return Math.max(1, Math.round(this.wordCount / 130));
  }

  get processedAt(): Date | null {
    return this.analysisData?.processed_at ? new Date(this.analysisData.processed_at) : null;
  }

  /**
   * Client-derived unified card from the analysis result — no agent round-trip.
   * The meeting surface folds into the single card model: sentiment → a `chart`
   * (bars) section, call performance → a `progress` (ring) section. Returns null
   * when no figures are present so `<nova-card>` is hidden. Max 2 sections.
   */
  get derivedCard(): CardResponse | null {
    if (!this.analysisData) return null;

    const sections: Section[] = [];

    // --- Sentiment breakdown (chart / bars) ---
    const sa = this.analysisData.sentiment_analysis;
    if (sa) {
      const pos = sa.positive_percent ?? 0;
      const neu = sa.neutral_percent ?? 0;
      const neg = sa.negative_percent ?? 0;
      if (pos !== 0 || neu !== 0 || neg !== 0) {
        sections.push({
          type: 'chart',
          variant: 'bars',
          title: this.#translate.instant('NOVA_SENTIMENT_BREAKDOWN'),
          unit: null,
          seriesLabels: null,
          items: [
            { label: this.#translate.instant('NOVA_SENTIMENT_POSITIVE'), value: pos, value2: null, display: `${pos}%` },
            { label: this.#translate.instant('NOVA_SENTIMENT_NEUTRAL'),  value: neu, value2: null, display: `${neu}%` },
            { label: this.#translate.instant('NOVA_SENTIMENT_NEGATIVE'), value: neg, value2: null, display: `${neg}%` },
          ],
        });
      }
    }

    // --- Call performance (progress / ring) ---
    // overall_performance_score is 0–1 decimal; multiply by 100 for 0–100 percent.
    const rawScore = this.analysisData.coaching_tips?.overall_performance_score ?? 0;
    const scorePercent = Math.min(100, Math.max(0, Math.round(rawScore * 100)));
    if (scorePercent > 0) {
      sections.push({
        type: 'progress',
        variant: 'ring',
        title: this.#translate.instant('NOVA_CALL_PERFORMANCE'),
        label: this.#translate.instant('NOVA_OVERALL_SCORE'),
        percent: scorePercent,
        detail: `${scorePercent}/100`,
        steps: null,
        bars: null,
      });
    }

    return sections.length ? { title: null, sections, actions: null } : null;
  }
}
